import { toast } from 'react-toastify'
import { v4 as uuidv4 } from 'uuid'

// Original transformation functions (can remain as they are or be utility within this file)

/**
 * Transforms conditions from the EdgeSidebar format to the RouteModal/ConditionBuilder format.
 * @param {Array<object>} edgeConditions - Array of condition objects from EdgeSidebar.
 * @returns {Array<object>} Array of condition objects/groups for RouteModal.
 */
export function transformEdgeConditionsToRouteConditions(edgeConditions) {
  if (!edgeConditions || edgeConditions.length === 0) {
    return []
  }

  const routeModalConditions = []
  let currentAndGroupConditions = []

  const finalizeAndGroup = () => {
    if (currentAndGroupConditions.length > 0) {
      if (currentAndGroupConditions.length === 1) {
        routeModalConditions.push(currentAndGroupConditions[0])
      }
      else {
        routeModalConditions.push({
          isGroup: true,
          operator: 'AND',
          conditions: [...currentAndGroupConditions],
        })
      }
      currentAndGroupConditions = []
    }
  }

  edgeConditions.forEach((ec, index) => {
    const mappedSimpleCondition = {
      field: ec.field,
      operator: ec.conditionOperator,
      value: ec.value,
      isGroup: false,
    }

    if (index === 0 || (ec.operator && ec.operator.toUpperCase() === 'AND')) {
      currentAndGroupConditions.push(mappedSimpleCondition)
    }
    else if (ec.operator && ec.operator.toUpperCase() === 'OR') {
      finalizeAndGroup()
      currentAndGroupConditions.push(mappedSimpleCondition)
    }
    else {
      finalizeAndGroup()
      currentAndGroupConditions.push(mappedSimpleCondition)
    }
  })

  finalizeAndGroup()
  return routeModalConditions
}

/**
 * Generates a human-readable label from RouteModal/ConditionBuilder formatted conditions.
 * @param {Array<object>} routeConditions - Array of condition objects/groups.
 * @returns {string} A descriptive label string.
 */
export function generateLabelFromRouteConditions(routeConditions) {
  if (!routeConditions || routeConditions.length === 0) {
    return 'No conditions'
  }

  return routeConditions
    .map((condition) => {
      if (condition.isGroup) {
        const groupOperator = condition.operator ? condition.operator.toUpperCase() : 'AND'
        const nestedConditionsString = condition.conditions
          .map(
            groupCond =>
              `${groupCond.field || ''} ${groupCond.operator || ''} ${groupCond.value !== undefined ? `"${groupCond.value}"` : ''}`.trim(),
          )
          .join(` ${groupOperator} `)
        return `(${nestedConditionsString})`
      }
      return `${condition.field || ''} ${condition.operator || ''} ${condition.value !== undefined ? `"${condition.value}"` : ''}`.trim()
    })
    .join(' OR ')
}

/**
 * Handles the full workflow of converting all advanced condition edges from a source node
 * to a new, single Route Node setup.
 * @param {object} params - Parameters for the conversion.
 * @param {object} params.edgeData - The data of one of the edges being converted (used to find the source node).
 * @param {Array<object>} params.currentConditions - NOTE: This is no longer directly used as we iterate over all eligible edges.
 * @param {object} params.elements - The current React Flow elements (nodes and edges).
 * @param {Function} params.triggerUpdate - Function to update React Flow elements.
 * @param {Function} params.setIsSideBarOpen - Function to close the EdgeSidebar.
 * @param {Function} params.setIsEditingEdge - Function to clear the editing edge state.
 */
export function convertEdgeToRouteNodeWorkflow({
  edgeData, // Used to identify the source node
  // currentConditions, // No longer primary input for conditions
  elements,
  triggerUpdate,
  setIsSideBarOpen,
  setIsEditingEdge,
}) {
  // --- Gather Necessary Data ---
  if (!edgeData || !elements?.nodes || !elements?.edges) {
    toast.error('Could not perform conversion: Missing critical data.')
    return
  }

  const sourceNodeId = edgeData.source
  const sourceNodeObject = elements.nodes.find(node => node.id === sourceNodeId)

  if (!sourceNodeObject) {
    toast.error('Could not find the source node for this pathway.')
    return
  }

  // Find all eligible outgoing edges from the source node that use conditions
  const eligibleEdgesToConvert = elements.edges.filter(
    edge => edge.source === sourceNodeId && edge.data?.condition && edge.data.condition.length > 0,
  )

  if (eligibleEdgesToConvert.length === 0) {
    toast.info('No advanced condition pathways found on this node to convert.')
    // Still close the sidebar as the button was clicked
    setIsSideBarOpen(false)
    setIsEditingEdge(null)
    return
  }

  // --- Create a Single New Route Node ---
  const newRouteNodeId = uuidv4()
  const newRouteNodeX = sourceNodeObject.position.x + 250
  const newRouteNodeY = sourceNodeObject.position.y

  const newRouteNodeData = {
    name: `Route from ${sourceNodeObject.data.name || 'source'}`,
    routes: [], // Initialize as empty, will be populated from eligible edges
    fallbackNodeId: null,
  }

  const newRouteToTargetEdges = []

  // --- Process Each Eligible Edge ---
  eligibleEdgesToConvert.forEach((eligibleEdge) => {
    const conditionsFromThisEdge = eligibleEdge.data.condition
    const routeNodeFormattedConditions = transformEdgeConditionsToRouteConditions(conditionsFromThisEdge)

    // Add to the Route Node's routes array
    newRouteNodeData.routes.push({
      targetNodeId: eligibleEdge.target,
      conditions: routeNodeFormattedConditions,
    })

    // Create a new edge from the New Route Node to this eligible edge's original target
    const newEdgeLabel = generateLabelFromRouteConditions(routeNodeFormattedConditions)
    const newEdgeToOriginalTargetId = `reactflow__edge-${newRouteNodeId}-${eligibleEdge.target}`
    newRouteToTargetEdges.push({
      id: newEdgeToOriginalTargetId,
      source: newRouteNodeId,
      target: eligibleEdge.target,
      type: 'custom',
      animated: true,
      data: {
        condition: routeNodeFormattedConditions,
        label: newEdgeLabel,
      },
    })
  })

  const newNodeObject = {
    id: newRouteNodeId,
    type: 'Route',
    position: { x: newRouteNodeX, y: newRouteNodeY },
    data: newRouteNodeData,
  }

  // --- Create the Edge from Original Source to New Route Node ---
  const edgeToNewRouteNode = {
    id: `reactflow__edge-${sourceNodeId}-${newRouteNodeId}`,
    source: sourceNodeId,
    target: newRouteNodeId,
    type: 'custom', // Or your default edge type
    animated: true,
    data: { label: 'user responded' }, // As per user's update
  }

  // --- Update React Flow State ---
  if (!triggerUpdate) { // elements already checked
    toast.error('Could not apply changes: Flow context is not available.')
    return
  }

  const updatedNodes = [...elements.nodes, newNodeObject]

  // Filter out all original eligible edges and add the new ones
  const eligibleEdgeIds = new Set(eligibleEdgesToConvert.map(e => e.id))
  const remainingEdges = elements.edges.filter(edge => !eligibleEdgeIds.has(edge.id))

  const updatedEdges = [
    ...remainingEdges,
    edgeToNewRouteNode,
    ...newRouteToTargetEdges,
  ]

  triggerUpdate({ nodes: updatedNodes, edges: updatedEdges }, false)

  // --- UI Feedback & Cleanup ---
  toast.success('Advanced pathways converted to a new Route Node!')
  setIsSideBarOpen(false)
  setIsEditingEdge(null)
}
